<template>
  <div class="usage" v-loading="loading">
    <div class="usage__bar">
      <div>
        <h2 class="dev-section__title">{{ title }}</h2>
        <div class="dev-section__sub">{{ subtitle }}</div>
      </div>
      <el-radio-group v-model="days" size="small" @change="load">
        <el-radio-button :label="7">7 days</el-radio-button>
        <el-radio-button :label="30">30 days</el-radio-button>
        <el-radio-button :label="90">90 days</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 指标 -->
    <div class="usage__tiles">
      <div class="usage__tile">
        <div class="usage__tile-label">Today</div>
        <div class="usage__tile-value vtm-num">{{ fmt(data.today) }}</div>
      </div>
      <div class="usage__tile">
        <div class="usage__tile-label">This month</div>
        <div class="usage__tile-value vtm-num">{{ fmt(data.thisMonth) }}</div>
      </div>
      <div class="usage__tile">
        <div class="usage__tile-label">Last {{ days }} days</div>
        <div class="usage__tile-value vtm-num">{{ fmt(data.total) }}</div>
      </div>
      <div class="usage__tile">
        <div class="usage__tile-label">{{ admin ? "Customers calling" : "Machines with API calls" }}</div>
        <div class="usage__tile-value vtm-num">{{ fmt(admin ? customers.length : namedMachines.length) }}</div>
      </div>
    </div>

    <!-- 每日柱状图 -->
    <div class="dev-card usage__chart-card">
      <div class="usage__chart-title">Calls per day</div>
      <div v-if="data.total" class="usage__chart" role="img" :aria-label="'Calls per day over the last ' + days + ' days'">
        <div class="usage__grid">
          <span class="vtm-num">{{ fmt(chartMax) }}</span>
          <span class="vtm-num">{{ fmt(Math.round(chartMax / 2)) }}</span>
          <span>0</span>
        </div>
        <div class="usage__bars">
          <div
            v-for="day in series"
            :key="day.date"
            class="usage__col"
            @mouseenter="hover = day"
            @mouseleave="hover = null"
          >
            <div class="usage__bar-fill" :class="{ 'is-hover': hover === day }" :style="{ height: barHeight(day.calls) }"></div>
          </div>
          <div v-if="hover" class="usage__tip" :style="tipStyle">
            <b class="vtm-num">{{ fmt(hover.calls) }}</b> calls
            <span>{{ fmtDay(hover.date) }}</span>
          </div>
        </div>
        <div class="usage__axis">
          <span>{{ fmtDay(series[0].date) }}</span>
          <span>{{ fmtDay(series[series.length - 1].date) }}</span>
        </div>
      </div>
      <div v-else class="usage__empty">
        <i class="el-icon-data-analysis"></i>
        <div>No API calls in the last {{ days }} days.</div>
        <div v-if="!admin" class="usage__empty-sub">Calls from your API keys and AI assistants show up here.</div>
      </div>
    </div>

    <div class="usage__tables">
      <!-- 管理员：按客户 -->
      <div v-if="admin" class="dev-card dev-card--flush usage__table-card usage__table-card--wide">
        <div class="usage__table-title">By customer</div>
        <el-table :data="customers" empty-text="No calls yet" size="small">
          <el-table-column label="Customer" min-width="220">
            <template slot-scope="{ row }">{{ row.email || row.userId }}</template>
          </el-table-column>
          <el-table-column label="Calls" min-width="140" align="right">
            <template slot-scope="{ row }">
              <div class="usage__share">
                <span class="vtm-num">{{ fmt(row.calls) }}</span>
                <span class="usage__share-bar"><i :style="{ width: share(row.calls, data.total) }"></i></span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Keys" width="70" align="right">
            <template slot-scope="{ row }"><span class="vtm-num">{{ row.keys }}</span></template>
          </el-table-column>
          <el-table-column label="Machines" width="90" align="right">
            <template slot-scope="{ row }"><span class="vtm-num">{{ row.machines }}</span></template>
          </el-table-column>
          <el-table-column label="Last call" min-width="150">
            <template slot-scope="{ row }">{{ row.lastUsedAt ? parseServerDate(row.lastUsedAt).toLocaleString() : "—" }}</template>
          </el-table-column>
          <el-table-column label="" width="90" align="right">
            <template slot-scope="{ row }">
              <el-button type="text" @click="$emit('drill', { userId: row.userId, email: row.email })">Details</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 按密钥 -->
      <div v-if="!admin" class="dev-card dev-card--flush usage__table-card">
        <div class="usage__table-title">By key</div>
        <el-table :data="data.byKey || []" empty-text="No keys yet" size="small">
          <el-table-column label="Key" min-width="180">
            <template slot-scope="{ row }">
              <span :class="{ 'usage__off': !row.isActive }">{{ row.label }}</span>
              <span v-if="isConnector(row)" class="usage__ai">AI</span>
            </template>
          </el-table-column>
          <el-table-column label="Calls" min-width="140" align="right">
            <template slot-scope="{ row }">
              <div class="usage__share">
                <span class="vtm-num">{{ fmt(row.calls) }}</span>
                <span class="usage__share-bar"><i :style="{ width: share(row.calls, data.total) }"></i></span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 按设备 -->
      <div class="dev-card dev-card--flush usage__table-card" :class="{ 'usage__table-card--wide': admin }">
        <div class="usage__table-title">
          By machine
          <el-tooltip content="A call that asks about several machines counts once for each, so this can add up to more than the total." placement="top">
            <i class="el-icon-info usage__info"></i>
          </el-tooltip>
        </div>
        <el-table :data="machineRows" empty-text="No machine-specific calls yet" size="small">
          <el-table-column label="Machine" min-width="200">
            <template slot-scope="{ row }">
              <template v-if="row.machineRef">
                <div class="usage__mname">{{ row.machineName || "Unknown machine" }}</div>
                <div class="usage__mno vtm-mono">{{ row.machineNo || row.machineRef }}</div>
              </template>
              <template v-else>
                <div class="usage__mname">All machines</div>
                <div class="usage__mno">Calls that didn't name a machine, like listing every machine</div>
              </template>
            </template>
          </el-table-column>
          <el-table-column label="Calls" min-width="140" align="right">
            <template slot-scope="{ row }">
              <div class="usage__share">
                <span class="vtm-num">{{ fmt(row.calls) }}</span>
                <span class="usage__share-bar"><i :style="{ width: share(row.calls, machineMax) }"></i></span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
  // 用量面板：指标 + 每日柱状图 + 按密钥/设备（管理员另有按客户）。
  // admin=true 看全部客户；userId 有值时管理员查看某个客户
  import { isConnectorKey, normalizeUsage, parseServerDate } from "./developerContent";

  export default {
    name: "UsagePanel",
    props: {
      admin: { type: Boolean, default: false },
      userId: { type: String, default: "" },
      title: { type: String, default: "Usage" },
      subtitle: { type: String, default: "Calls made with your API keys and AI assistants" }
    },
    data() {
      return {
        days: 30,
        loading: false,
        hover: null,
        data: {}
      };
    },
    computed: {
      // 补齐没有调用的日期，柱状图按天连续
      series() {
        const byDate = {};
        (this.data.daily || []).forEach(d => (byDate[d.date] = d.calls));
        const out = [];
        const end = this.data.daily && this.data.daily.length ? this.data.daily[this.data.daily.length - 1].date : null;
        const last = end && end > this.todayUtc() ? new Date(end + "T00:00:00Z") : new Date(this.todayUtc() + "T00:00:00Z");
        for (let i = this.days - 1; i >= 0; i--) {
          const d = new Date(last.getTime() - i * 86400000).toISOString().slice(0, 10);
          out.push({ date: d, calls: byDate[d] || 0 });
        }
        return out;
      },
      chartMax() {
        const max = Math.max(0, ...this.series.map(d => d.calls));
        if (max <= 10) return 10;
        const mag = Math.pow(10, Math.floor(Math.log10(max)));
        return Math.ceil(max / mag) * mag;
      },
      tipStyle() {
        const idx = this.series.indexOf(this.hover);
        const pct = ((idx + 0.5) / this.series.length) * 100;
        return { left: pct + "%", transform: pct > 80 ? "translateX(-100%)" : pct < 20 ? "none" : "translateX(-50%)" };
      },
      customers() {
        return this.data.byCustomer || [];
      },
      machineRows() {
        return this.data.byMachine || [];
      },
      namedMachines() {
        return this.machineRows.filter(m => m.machineRef);
      },
      machineMax() {
        return Math.max(1, ...this.machineRows.map(m => m.calls));
      }
    },
    watch: {
      userId() {
        this.load();
      }
    },
    created() {
      this.load();
    },
    methods: {
      load() {
        this.loading = true;
        const params = { days: this.days };
        if (this.userId) {
          params.userId = this.userId;
        }
        const url = this.admin ? this.$url.ApiKey.adminUsage : this.$url.ApiKey.usage;
        this.$api
          .request(url, this.$method.get, params)
          .then(res => {
            if (res && res.code === 0) {
              this.data = normalizeUsage(res.data || {});
            }
          })
          .finally(() => (this.loading = false));
      },
      todayUtc() {
        return new Date().toISOString().slice(0, 10);
      },
      barHeight(calls) {
        if (!calls) return "0";
        return Math.max(2, (calls / this.chartMax) * 100) + "%";
      },
      share(value, of) {
        return of ? Math.min(100, (value / of) * 100) + "%" : "0";
      },
      parseServerDate,
      fmt(n) {
        return (n || 0).toLocaleString();
      },
      fmtDay(date) {
        return new Date(date + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
      },
      isConnector(row) {
        return isConnectorKey(row);
      }
    }
  };
</script>

<style scoped lang="scss">
  .usage__bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 14px;
  }

  .usage__tiles {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 12px;

    @media (max-width: 900px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .usage__tile {
    background: var(--vtm-surface);
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-lg);
    padding: 14px 16px;
    box-shadow: var(--vtm-e1);
  }

  .usage__tile-label {
    font-size: 12.5px;
    color: var(--vtm-ink-2);
  }

  .usage__tile-value {
    margin-top: 4px;
    font-family: var(--vtm-display);
    font-size: 24px;
    font-weight: 650;
    color: var(--vtm-heading);
  }

  .usage__chart-card {
    margin-bottom: 12px;
  }

  .usage__chart-title,
  .usage__table-title {
    font-size: 13.5px;
    font-weight: 650;
    color: var(--vtm-heading);
  }

  .usage__table-title {
    padding: 12px 16px;
    border-bottom: 1px solid var(--vtm-line);
  }

  .usage__chart {
    position: relative;
    margin-top: 14px;
    padding-left: 44px;
  }

  .usage__grid {
    position: absolute;
    left: 0;
    top: 0;
    height: 160px;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    pointer-events: none;

    span {
      font-size: 11px;
      color: var(--vtm-ink-3);
      line-height: 0;
      border-bottom: 1px dashed var(--vtm-line);
      padding-bottom: 0;
      width: 100%;
      display: block;
      position: relative;
    }
  }

  .usage__bars {
    position: relative;
    height: 160px;
    display: flex;
    align-items: flex-end;
    gap: 2px;
  }

  .usage__col {
    flex: 1;
    height: 100%;
    display: flex;
    align-items: flex-end;
    cursor: default;
  }

  .usage__bar-fill {
    width: 100%;
    background: var(--vtm-mint-dim);
    border-radius: 3px 3px 0 0;
    transition: background var(--vtm-speed);

    &.is-hover {
      background: var(--vtm-brand-teal);
    }
  }

  :root[data-theme="dark"] .usage__bar-fill.is-hover {
    background: var(--vtm-mint);
  }

  .usage__tip {
    position: absolute;
    top: -6px;
    background: var(--vtm-navy);
    color: #fff;
    font-size: 12px;
    padding: 6px 9px;
    border-radius: var(--vtm-r-sm);
    white-space: nowrap;
    pointer-events: none;
    box-shadow: var(--vtm-e2);

    span {
      display: block;
      color: #a9bcce;
      font-size: 11.5px;
    }
  }

  .usage__axis {
    display: flex;
    justify-content: space-between;
    margin-top: 6px;
    font-size: 11.5px;
    color: var(--vtm-ink-3);
  }

  .usage__empty {
    padding: 36px 0 28px;
    text-align: center;
    color: var(--vtm-ink-2);
    font-size: 13.5px;

    i {
      font-size: 30px;
      color: var(--vtm-ink-3);
      margin-bottom: 8px;
    }
  }

  .usage__empty-sub {
    font-size: 12.5px;
    color: var(--vtm-ink-3);
    margin-top: 4px;
  }

  .usage__tables {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;

    @media (max-width: 1000px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .usage__table-card--wide {
    grid-column: 1 / -1;
  }

  .usage__share {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }

  .usage__share-bar {
    width: 60px;
    height: 6px;
    border-radius: 3px;
    background: var(--vtm-well);
    overflow: hidden;
    flex-shrink: 0;

    i {
      display: block;
      height: 100%;
      background: var(--vtm-mint-dim);
      border-radius: 3px;
    }
  }

  .usage__mname {
    font-weight: 600;
    color: var(--vtm-ink);
  }

  .usage__mno {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }

  .usage__off {
    color: var(--vtm-ink-3);
    text-decoration: line-through;
  }

  .usage__ai {
    margin-left: 6px;
    font-size: 10.5px;
    font-weight: 700;
    color: var(--vtm-info);
    background: var(--vtm-info-soft);
    border-radius: 4px;
    padding: 1px 5px;
  }

  .usage__info {
    color: var(--vtm-ink-3);
    margin-left: 4px;
    cursor: help;
  }
</style>
